import type { DeadLetterRow, EventRow, Profile, RepwiseStore } from './store.ts';
import { TransportError, type PulledEvent, type SyncTransport, type WireEvent } from './transport.ts';
import { uuidv7 } from './uuid.ts';
import { canAttempt, CLOSED, DEFAULT_RETRY, onFailure, onSuccess, type Breaker, type RetryPolicy, type Trigger } from './retry.ts';

export type SyncState = 'synced' | 'pending' | 'syncing' | 'offline' | 'signed_out' | 'needs_claim' | 'attention';

export interface SyncStatus {
  state: SyncState;
  /** A drain is in flight. The label does NOT change for this alone (no flicker); show a subtle indicator instead. */
  syncing: boolean;
  pending: number;
  deadLetters: number;
  lastSyncAt?: number;
  /** User-facing copy, exactly as ui_ux spec C10. */
  label: string;
  /** ADR-0022: present after a failed attempt. `nextAt` is when automatic sync tries again. */
  retry?: { mode: 'closed' | 'open'; failures: number; nextAt: number };
  /** The device has no connection right now (isOnline): changes sync when it's back. */
  deviceOffline?: boolean;
  /** Oldest unsynced change (epoch ms), for the "waiting for 2 days" nudge (J11). */
  oldestPendingAt?: number;
}

export interface DrainResult {
  pushed: number;
  pulled: number;
  deadLettered: number;
  /** `waiting`: inside the backoff or with the breaker open, so no attempt was made (ADR-0022). */
  skipped?: 'signed_out' | 'needs_claim' | 'busy' | 'offline' | 'auth' | 'waiting';
}

export interface EngineOptions {
  /** The signed-in Supabase user id, or null when signed out. */
  getUserId: () => string | null;
  now?: () => number;
  batchSize?: number;
  pullLimit?: number;
  /** ADR-0022: backoff then circuit breaker. */
  retry?: RetryPolicy;
  random?: () => number;
  /**
   * Whether the device has a connection (navigator.onLine). A failure while it has none is the gym basement, not a
   * struggling server: it doesn't feed the breaker, since coming back online probes at once (phone test, 14 Oct 2026).
   */
  isOnline?: () => boolean;
}

const LOCK = 'repwise-sync';

/** In-process fallback when the Web Locks API isn't available (tests, older browsers). */
let chain: Promise<unknown> = Promise.resolve();
let busy = false;

async function withLock<T>(fn: () => Promise<T>): Promise<T | 'busy'> {
  const locks = (globalThis.navigator as Navigator | undefined)?.locks;
  if (locks?.request) {
    return (await locks.request(LOCK, { ifAvailable: true }, async (lock) => (lock ? fn() : 'busy'))) as T | 'busy';
  }
  if (busy) return 'busy';
  busy = true;
  const run = chain.then(fn).finally(() => {
    busy = false;
  });
  chain = run.catch(() => undefined);
  return run;
}


/** " (2 days)" once changes have waited more than a day: the nudge before storage eviction can bite (J11). */
function ageSuffix(oldest: number | undefined, now: number): string {
  if (oldest === undefined) return '';
  const days = Math.floor((now - oldest) / 86_400_000);
  return days >= 1 ? ` (${days} day${days === 1 ? '' : 's'})` : '';
}

/**
 * Drains the outbox to the transport and pulls new events (docs/04-sync-protocol.md §5).
 * One drain at a time; network failures back off and never dead-letter (offline is normal in a gym);
 * per-row conflicts/rejections go to the dead-letter store and never block the rest.
 */
export class SyncEngine {
  private state: SyncState | null = null;
  private breaker: Breaker = CLOSED;
  private inFlight = false;
  private readonly statusListeners = new Set<() => void>();
  private readonly now: () => number;
  private readonly batch: number;
  private readonly pullLimit: number;

  constructor(
    private readonly store: RepwiseStore,
    private readonly transport: SyncTransport,
    private readonly opts: EngineOptions,
  ) {
    this.now = opts.now ?? Date.now;
    this.batch = opts.batchSize ?? 100;
    this.pullLimit = opts.pullLimit ?? 500;
  }

  /**
   * Sync-status changes (syncing, offline, pending counts). Deliberately separate from the store's DATA
   * channel: auto-sync listens to data changes, so mixing the two created a drain → notify → drain loop.
   */
  onStatus(cb: () => void): () => void {
    this.statusListeners.add(cb);
    return () => this.statusListeners.delete(cb);
  }

  private emitStatus(): void {
    for (const cb of this.statusListeners) cb();
  }

  /**
   * Bind the active profile to the signed-in user (ADR-0004 hardening). Returns what the UI must do:
   * 'ok' — ready to sync; 'needs_claim' — this device holds unclaimed local workouts; ask before attaching.
   * A profile claimed by a different user is never pushed: we switch to (or create) that user's own profile.
   */
  async bindUser(): Promise<'ok' | 'needs_claim' | 'signed_out'> {
    const user = this.opts.getUserId();
    if (!user) return 'signed_out';
    const db = this.store.db;
    const active = await this.store.activeProfile();
    if (active.claimedBy === user) return 'ok';
    if (active.claimedBy && active.claimedBy !== user) {
      const own = await db.profiles.where('claimedBy').equals(user).first();
      const target: Profile = own ?? { id: uuidv7(), claimedBy: user, createdAt: this.now() };
      await db.transaction('rw', db.profiles, db.meta, async () => {
        await db.profiles.put(target);
        await db.meta.put({ key: 'activeProfile', value: target.id });
        await db.meta.put({ key: `pullCursor:${target.id}`, value: (await db.meta.get(`pullCursor:${target.id}`))?.value ?? 0 });
      });
      this.store.notify();
      return 'ok';
    }
    // Unclaimed: attach silently only if there is nothing local to attach.
    const local = await db.events.where('ownerId').equals(active.id).count();
    if (local === 0) {
      await db.profiles.put({ ...active, claimedBy: user });
      return 'ok';
    }
    return 'needs_claim';
  }

  /** What an explicit claim would attach ("Attach 3 workouts on this device to s•••@gmail.com?"). */
  async claimPreview(): Promise<{ events: number; workouts: number }> {
    const active = await this.store.activeProfile();
    const rows = await this.store.db.events.where('ownerId').equals(active.id).toArray();
    return { events: rows.length, workouts: rows.filter((r) => r.type === 'workout_finished').length };
  }

  /** The user said yes: attach this device's local data to their account. */
  async claim(): Promise<void> {
    const user = this.opts.getUserId();
    if (!user) throw new Error('Sign in before claiming local data');
    const active = await this.store.activeProfile();
    if (active.claimedBy && active.claimedBy !== user) throw new Error('This data belongs to another account');
    await this.store.db.profiles.put({ ...active, claimedBy: user });
    this.store.notify();
  }

  /** The user chose "Use account data": keep local unsynced data recoverable in dead-letter, start a fresh profile. */
  async useAccountData(): Promise<void> {
    const user = this.opts.getUserId();
    if (!user) throw new Error('Sign in first');
    const db = this.store.db;
    const old = await this.store.activeProfile();
    const fresh: Profile = { id: uuidv7(), claimedBy: user, createdAt: this.now() };
    await db.transaction('rw', db.profiles, db.meta, db.outbox, db.deadLetter, db.events, async () => {
      const rows = await db.events.where('ownerId').equals(old.id).toArray();
      for (const r of rows) await db.deadLetter.put({ id: r.id, ownerId: fresh.id, at: this.now(), reason: 'discarded', payload: r.payload, detail: 'kept from this device when choosing account data' });
      await db.outbox.where('ownerId').equals(old.id).delete();
      await db.profiles.put(fresh);
      await db.meta.put({ key: 'activeProfile', value: fresh.id });
    });
    this.store.notify();
  }

  /** Back online (or the user tapped "Sync now"): forget server back-off so the next drain tries everything, in order. */
  async retryNow(trigger: Trigger = 'manual'): Promise<DrainResult> {
    const profile = await this.store.activeProfile(); // other owners' rows stay quarantined as they were
    await this.store.db.outbox.where('ownerId').equals(profile.id).modify({ nextAttemptAt: 0 }); // legacy per-row timers
    return this.drain(trigger);
  }

  /** When automatic sync may try next (0 = now). The scheduler wakes for it (ADR-0022). */
  nextAttemptAt(): number {
    return this.breaker.nextAt;
  }

  /** Push then pull. Safe to call often: concurrent calls collapse into one. */
  /**
   * `trigger` (ADR-0022): `auto` (writes, timers) respects the backoff and the open breaker; `manual` (Sync now),
   * `online` and `start` may always probe. Explicit calls default to manual.
   */
  async drain(trigger: Trigger = 'manual'): Promise<DrainResult> {
    if (!canAttempt(this.breaker, this.now(), trigger)) return { pushed: 0, pulled: 0, deadLettered: 0, skipped: 'waiting' };
    const r = await withLock(() => this.drainLocked());
    return r === 'busy' ? { pushed: 0, pulled: 0, deadLettered: 0, skipped: 'busy' } : r;
  }

  private async drainLocked(): Promise<DrainResult> {
    const bound = await this.bindUser();
    if (bound !== 'ok') {
      this.state = bound;
      this.emitStatus();
      return { pushed: 0, pulled: 0, deadLettered: 0, skipped: bound };
    }
    this.state = null;
    this.inFlight = true;
    this.emitStatus();
    const result: DrainResult = { pushed: 0, pulled: 0, deadLettered: 0 };
    try {
      await this.push(result);
      await this.pull(result);
      await this.store.setMeta('lastSyncAt', this.now());
      this.state = null;
      this.breaker = onSuccess();
    } catch (e) {
      const kind = e instanceof TransportError ? e.kind : 'server';
      this.state = kind === 'auth' ? 'signed_out' : 'offline';
      result.skipped = kind === 'auth' ? 'auth' : 'offline';
      // Network and server failures feed the breaker; a lapsed session is its own state (the user must sign in).
      const deviceOffline = this.opts.isOnline ? !this.opts.isOnline() : false;
      if (kind !== 'auth' && !deviceOffline) this.breaker = onFailure(this.breaker, this.now(), this.opts.retry ?? DEFAULT_RETRY, this.opts.random ?? Math.random);
    }
    this.inFlight = false;
    this.emitStatus();
    return result;
  }

  private async push(result: DrainResult): Promise<void> {
    const db = this.store.db;
    const user = this.opts.getUserId()!;
    const profile = await this.store.activeProfile();
    if (profile.claimedBy !== user) return; // quarantine: never push another owner's rows
    const deviceId = await this.store.deviceId();
    for (;;) {
      const now = this.now();
      // Push strictly in device order: only the unbroken prefix that is due. Skipping a backed-off row and pushing
      // later ones would give other devices a server order (and so a replay order) different from this phone's.
      const queue = await db.outbox.where('ownerId').equals(profile.id).sortBy('deviceSeq');
      const firstWaiting = queue.findIndex((o) => o.nextAttemptAt > now);
      const due = (firstWaiting === -1 ? queue : queue.slice(0, firstWaiting)).slice(0, this.batch);
      if (due.length === 0) return;
      const rows = (await db.events.bulkGet(due.map((o) => o.id))).filter((r): r is EventRow => !!r);
      const wire: WireEvent[] = rows.map((r) => ({
        id: r.id,
        device_id: r.deviceId || deviceId,
        device_seq: r.deviceSeq,
        type: r.type,
        workout_id: r.workoutId,
        at: new Date(r.at).toISOString(),
        payload: r.payload,
        hash: r.hash,
      }));
      let statuses;
      try {
        statuses = await this.transport.push(wire);
      } catch (e) {
        // No signal (a basement gym, a lift, a tunnel) is normal, not a fault of these rows: record it, but don't
        // back off, so the first drain after reconnecting sends everything. Back-off is for real server errors.
        // Retry timing is the engine-level breaker's job (ADR-0022); rows only record what happened, and they
        // never back off individually, so order is kept and the first good attempt sends everything.
        const offline = e instanceof TransportError && e.kind === 'offline';
        await db.transaction('rw', db.outbox, async () => {
          for (const o of due) await db.outbox.update(o.id, offline ? { lastError: 'offline' } : { attempts: o.attempts + 1, lastError: e instanceof Error ? e.message : String(e) });
        });
        throw e;
      }
      const byId = new Map(statuses.map((s) => [s.id, s.status]));
      await db.transaction('rw', db.outbox, db.deadLetter, async () => {
        for (const r of rows) {
          const st = byId.get(r.id);
          if (st === 'inserted' || st === 'duplicate') {
            await db.outbox.delete(r.id);
            result.pushed++;
          } else if (st === 'conflict' || st === 'rejected') {
            await db.outbox.delete(r.id);
            await db.deadLetter.put({ id: r.id, ownerId: r.ownerId, at: now, reason: st, payload: r.payload });
            result.deadLettered++;
          }
        }
      });
      if (due.length < this.batch) return;
    }
  }

  /** Has this profile completed at least one full pull? Onboarding is blocked until it has (ADR-0016). */
  async hasPulledOnce(): Promise<boolean> {
    const p = await this.store.activeProfile();
    return (await this.store.meta<boolean>(`pulledOnce:${p.id}`)) === true;
  }

  private async pull(result: DrainResult): Promise<void> {
    const db = this.store.db;
    const profile = await this.store.activeProfile();
    const cursorKey = `pullCursor:${profile.id}`;
    let since = (await this.store.meta<number>(cursorKey)) ?? 0;
    const finished = async () => {
      if (!(await this.store.meta<boolean>(`pulledOnce:${profile.id}`))) {
        await this.store.setMeta(`pulledOnce:${profile.id}`, true);
        this.store.notify(); // a gate transition (restoring → onboarding/app) is a data-level change
      }
    };
    for (;;) {
      const page: PulledEvent[] = await this.transport.pull(since, this.pullLimit);
      if (page.length === 0) return finished();
      const pulledBefore = result.pulled;
      await db.transaction('rw', db.events, db.deadLetter, db.meta, async () => {
        for (const r of page) {
          const local = await db.events.get(r.id);
          if (local) {
            if (local.hash === r.hash) await db.events.update(r.id, { serverSeq: r.server_seq });
            else if (!(await db.deadLetter.get(r.id))) {
              // Keep the first record of a clash (e.g. a push 'conflict' already filed it); never overwrite.
              await db.deadLetter.add({ id: r.id, ownerId: profile.id, at: this.now(), reason: 'pull_conflict', payload: r.payload, detail: 'server copy differs from this device' });
            }
          } else {
            await db.events.add({
              id: r.id,
              ownerId: profile.id,
              deviceId: r.device_id,
              deviceSeq: r.device_seq,
              serverSeq: r.server_seq,
              at: Date.parse(r.at),
              type: r.type,
              workoutId: r.workout_id,
              hash: r.hash,
              payload: r.payload,
            });
            result.pulled++;
          }
          since = Math.max(since, r.server_seq);
        }
        await db.meta.put({ key: cursorKey, value: since });
      });
      // Only NEW rows are a data change; confirming server_seq on our own rows is not (avoids a re-drain loop).
      if (result.pulled > pulledBefore) this.store.notify();
      if (page.length < this.pullLimit) return finished();
    }
  }

  async status(): Promise<SyncStatus> {
    const pending = await this.store.pendingCount();
    const dead = (await this.store.deadLetters()).length;
    const oldestPendingAt = await this.store.oldestPendingAt();
    const lastSyncAt = await this.store.meta<number>('lastSyncAt');
    const signedIn = !!this.opts.getUserId();
    let state: SyncState;
    if (!signedIn) state = 'signed_out';
    else if (this.state === 'needs_claim' || this.state === 'offline' || this.state === 'signed_out') state = this.state;
    else if (dead > 0) state = 'attention';
    else state = pending > 0 ? 'pending' : 'synced';
    const label = (
      {
        synced: 'Synced',
        pending: this.inFlight ? `Syncing ${pending === 1 ? '1 change' : `${pending} changes`}…` : `${pending === 1 ? '1 change' : `${pending} changes`} waiting to sync${ageSuffix(oldestPendingAt, this.now())}`,
        syncing: 'Syncing',
        offline: this.breaker.mode === 'open' ? 'Sync paused' : 'Sync paused, retrying',
        signed_out: 'Sign in to sync. Your workouts are safe on this device',
        needs_claim: 'Choose what to sync',
        attention: dead === 1 ? "1 change couldn't sync" : `${dead} changes couldn't sync`,
      } as const
    )[state];
    const b = this.breaker;
    const retry = b.failures > 0 ? { mode: b.mode, failures: b.failures, nextAt: b.nextAt } : undefined;
    const deviceOffline = this.opts.isOnline ? !this.opts.isOnline() : false;
    return { state, syncing: this.inFlight, pending, deadLetters: dead, ...(retry ? { retry } : {}), ...(deviceOffline ? { deviceOffline } : {}), ...(oldestPendingAt !== undefined ? { oldestPendingAt } : {}), ...(lastSyncAt !== undefined ? { lastSyncAt } : {}), label };
  }
}

export type { DeadLetterRow };
